import { useEffect, useState } from 'react'
import { Link2, Loader2 } from 'lucide-react'
import { SectionCard } from '@/components/ui/SectionCard'
import { showApiError } from '@/lib/utils/notifications'
import { YardiResidentSyncPanel } from './YardiResidentSyncPanel'
import { ResidentMatchStatusCard } from './ResidentMatchStatusCard'
import { YardiTCodeMappingControls } from './YardiTCodeMappingControls'
import { fetchYardiPropertyContext } from '../../services/yardiResidentService'
import type { ResidentPmsIntegrationTabProps } from '../../types'

function ResidentPmsIntegrationTabBody({
  propertyId,
  residentId,
  currentTCode,
  onUpdated,
}: ResidentPmsIntegrationTabProps) {
  const [companyId, setCompanyId] = useState<string | number>('')
  const [propertyCode, setPropertyCode] = useState('')
  const [propertyName, setPropertyName] = useState('')
  const [contextLoading, setContextLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    void fetchYardiPropertyContext(propertyId)
      .then((context) => {
        if (cancelled) return
        setCompanyId(context.companyId)
        setPropertyCode(context.propertyCode)
        setPropertyName(context.propertyName)
      })
      .catch(showApiError)
      .finally(() => {
        if (!cancelled) setContextLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [propertyId])

  return (
    <div className="space-y-5" data-testid="resident-pms-integration-tab">
      <SectionCard icon={Link2} title="PMS Integration">
        {contextLoading ? (
          <div className="flex items-center gap-2 py-6 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading Yardi property configuration…
          </div>
        ) : (
          <YardiResidentSyncPanel
            propertyId={propertyId}
            companyId={companyId}
            propertyCode={propertyCode}
            propertyName={propertyName}
            residentId={residentId}
            onConfirmed={onUpdated}
            refreshContextBeforeRequest
          />
        )}
      </SectionCard>

      {companyId && propertyCode ? (
        <ResidentMatchStatusCard
          propertyId={propertyId}
          companyId={companyId}
          propertyCode={propertyCode}
          residentDefaultId={residentId}
          contextLoading={contextLoading}
        />
      ) : null}

      <SectionCard icon={Link2} title="Update Residents Mapping">
        <YardiTCodeMappingControls
          propertyId={propertyId}
          residentDefaultId={residentId}
          companyId={companyId}
          propertyCode={propertyCode}
          currentTCode={currentTCode}
          onMapped={onUpdated}
          disabled={contextLoading}
        />
      </SectionCard>
    </div>
  )
}

export function ResidentPmsIntegrationTab(props: ResidentPmsIntegrationTabProps) {
  return <ResidentPmsIntegrationTabBody key={String(props.propertyId)} {...props} />
}
